import React, { useState, useEffect } from 'react';
import { getTiers, getMyTier } from '../services/api';
import './Tiers.css';

function Tiers() {
  const [tiers, setTiers] = useState([]);
  const [myTier, setMyTier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, myTierRes] = await Promise.all([
        getTiers(),
        getMyTier()
      ]);

      setTiers(tiersRes.data.sort((a, b) => a.minPoints - b.minPoints));
      setMyTier(myTierRes.data);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="container tiers-page">
      <div className="page-header">
        <h1>⭐ VIP Membership Tiers</h1>
        <p>Unlock exclusive benefits as you level up</p>
      </div>

      {/* Current Tier Status */}
      <div className="current-tier-card card">
        <div className="current-tier-header">
          <h2>Your Current Tier</h2>
          <div className="tier-badge" style={{ background: myTier?.currentTier?.color }}>
            {myTier?.currentTier?.name}
          </div>
        </div>
        
        <div className="tier-stats">
          <div className="tier-stat">
            <span className="stat-label">Lifetime Points</span>
            <span className="stat-number">{myTier?.lifetimePoints || 0}</span>
          </div>
          <div className="tier-stat">
            <span className="stat-label">Points Multiplier</span>
            <span className="stat-number">{myTier?.currentTier?.multiplier}x</span>
          </div>
        </div>

        {myTier?.nextTier && (
          <div className="next-tier-progress">
            <h3>Progress to {myTier.nextTier.name}</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${Math.min(myTier.progress, 100)}%`,
                  background: myTier.nextTier.color
                }}
              >
                <span className="progress-percentage">{Math.round(myTier.progress)}%</span>
              </div>
            </div>
            <p className="progress-info">
              {myTier.pointsToNextTier} more points needed
            </p>
          </div>
        )}
      </div>

      {/* All Tiers */}
      <div className="tiers-grid">
        {tiers.map(tier => {
          const isCurrentTier = tier.id === myTier?.currentTier?.id;
          const isUnlocked = myTier?.lifetimePoints >= tier.minPoints;
          
          return (
            <div 
              key={tier.id} 
              className={`tier-card ${isCurrentTier ? 'current' : ''} ${isUnlocked ? 'unlocked' : 'locked'}`}
              style={{ borderColor: tier.color }}
            >
              <div className="tier-card-header" style={{ background: tier.color }}>
                <h3>{tier.name}</h3>
                {isCurrentTier && <span className="current-badge">Current</span>}
                {isUnlocked && !isCurrentTier && <span className="unlocked-badge">✓ Unlocked</span>}
              </div>

              <div className="tier-card-body">
                <div className="tier-requirement">
                  <span className="requirement-label">Required Points:</span>
                  <span className="requirement-value">{tier.minPoints.toLocaleString()}</span>
                </div>

                <div className="tier-multiplier">
                  <span className="multiplier-icon">⚡</span>
                  <span className="multiplier-text">{tier.multiplier}x Points Multiplier</span>
                </div>

                <div className="tier-benefits">
                  <h4>Benefits:</h4>
                  <ul>
                    {tier.benefits.map((benefit, index) => (
                      <li key={index}>
                        <span className="benefit-icon">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* How It Works */}
      <div className="card how-it-works">
        <h2>How VIP Tiers Work</h2>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-icon">🛍️</div>
            <h3>Shop & Earn</h3>
            <p>Make purchases to earn points based on your tier multiplier</p>
          </div>
          <div className="info-item">
            <div className="info-icon">📈</div>
            <h3>Level Up</h3>
            <p>Accumulate lifetime points to unlock higher tiers</p>
          </div>
          <div className="info-item">
            <div className="info-icon">🎁</div>
            <h3>Enjoy Benefits</h3>
            <p>Access exclusive rewards and perks at each tier</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tiers;
